import React from "react";
import { Tooltip } from "antd";
import { WarningFilled } from "@ant-design/icons";
import { DayStats } from "./aggregations";

interface Props {
    date: Date;
    stats: DayStats | null;
    isSelected: boolean;
    isToday: boolean;
    onClick: () => void;
}

// Heatmap: 0% red → 50% amber → 100% green. Slightly above 100% shows
// orange (overfilled). Returns a CSS color usable as a left-edge accent.
function fillTint(pct: number): string {
    if (pct > 100) return "#ff7a45";
    const clamped = Math.max(0, Math.min(100, pct));
    if (clamped < 50) {
        const t = clamped / 50;
        return interpolate("#ff4d4f", "#faad14", t);
    }
    const t = (clamped - 50) / 50;
    return interpolate("#faad14", "#52c41a", t);
}

function interpolate(a: string, b: string, t: number): string {
    const ah = parseInt(a.slice(1), 16);
    const bh = parseInt(b.slice(1), 16);
    const ar = (ah >> 16) & 255, ag = (ah >> 8) & 255, ab = ah & 255;
    const br = (bh >> 16) & 255, bg = (bh >> 8) & 255, bb = bh & 255;
    const r = Math.round(ar + (br - ar) * t);
    const g = Math.round(ag + (bg - ag) * t);
    const b2 = Math.round(ab + (bb - ab) * t);
    return `rgb(${r}, ${g}, ${b2})`;
}

const DayCell: React.FC<Props> = ({ date, stats, isSelected, isToday, onClick }) => {
    const dayNum = date.getDate();
    const empty = stats == null;
    const fillPct = stats?.fillPct ?? 0;
    const overfilled = fillPct > 100;
    const tint = empty ? "#e8e8e8" : fillTint(fillPct);
    const violations = stats?.maxPerDayViolations.length ?? 0;
    const stripes = stats?.advertiserCounts.slice(0, 6) ?? [];

    const tooltip = empty ? "No slots scheduled" : (
        <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            <div><b>{stats!.dayOfWeek}, {stats!.dateStr}</b></div>
            <div>Breaks: {stats!.slotCount}</div>
            <div>Fill: {fillPct.toFixed(1)}% ({stats!.totalFilled}s / {stats!.totalCapacity}s)</div>
            <div>Unfilled: {stats!.totalUnfilled}s</div>
            <div>Total: ${stats!.totalDollars.toLocaleString()}</div>
            {violations > 0 && (
                <div style={{ color: "#ff4d4f" }}>
                    Max-per-day violations: {violations}
                </div>
            )}
        </div>
    );

    const borderColor = isSelected
        ? "#47cb38"
        : violations > 0
            ? "#ff4d4f"
            : "#e8e8e8";

    return (
        <Tooltip title={tooltip} mouseEnterDelay={0.4}>
            <div
                onClick={onClick}
                style={{
                    minHeight: 110,
                    padding: 8,
                    border: `${isSelected ? 2 : 1}px solid ${borderColor}`,
                    borderRadius: 4,
                    background: empty ? "#fafafa" : "#ffffff",
                    cursor: empty ? "default" : "pointer",
                    position: "relative",
                    transition: "border-color 120ms, box-shadow 120ms",
                    boxShadow: isSelected ? "0 0 0 1px #47cb38" : "0 1px 2px rgba(0,0,0,0.04)",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        bottom: 0,
                        width: 4,
                        background: tint,
                        borderTopLeftRadius: 4,
                        borderBottomLeftRadius: 4,
                    }}
                />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4, marginLeft: 4 }}>
                    <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: isToday ? "#47cb38" : "#262626",
                    }}>
                        {dayNum}
                    </div>
                    {violations > 0 && (
                        <WarningFilled style={{ color: "#ff4d4f", fontSize: 12 }} />
                    )}
                </div>
                {!empty && (
                    <div style={{ marginLeft: 4 }}>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 2 }}>
                            {stats!.slotCount} break{stats!.slotCount === 1 ? "" : "s"}
                        </div>
                        <div style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: overfilled ? "#fa8c16" : (fillPct >= 95 ? "#389e0d" : (fillPct >= 70 ? "#d48806" : "#cf1322")),
                            marginBottom: 4,
                        }}>
                            {fillPct.toFixed(0)}%
                        </div>
                        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 6 }}>
                            ${stats!.totalDollars.toLocaleString()}
                        </div>
                        <div style={{ display: "flex", gap: 2, height: 4 }}>
                            {stripes.map((s) => (
                                <div
                                    key={s.advertiserId}
                                    title={`${s.advertiserId}: ${s.count}`}
                                    style={{
                                        flex: s.count,
                                        background: s.color,
                                        borderRadius: 1,
                                        border: "1px solid rgba(0,0,0,0.04)",
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Tooltip>
    );
};

export default DayCell;
